import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogIn, LogOut, Clock, CalendarCheck, Wallet, User } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export default function MeDashboard() {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/me/dashboard");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const checkIn = async () => {
    try { await api.post("/me/check-in"); toast.success("Checked in"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const checkOut = async () => {
    try { await api.post("/me/check-out"); toast.success("Checked out"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const profile = data?.profile;
  const today = data?.today_attendance;
  const checkedIn = !!today?.check_in;
  const checkedOut = !!today?.check_out;

  return (
    <div className="space-y-8" data-testid="me-dashboard">
      <div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">My space</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight">
          Welcome, {profile?.full_name?.split(" ")[0] || "—"}.
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          {profile?.designation} · {profile?.department} · <span className="font-mono">{profile?.employee_code}</span>
        </p>
      </div>

      <div className="bg-white border border-zinc-200 p-6 rounded-sm">
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Today's attendance</div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Check-in</div>
              <div className="font-mono text-2xl font-bold">{fmtTime(today?.check_in)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Check-out</div>
              <div className="font-mono text-2xl font-bold">{fmtTime(today?.check_out)}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={checkIn} disabled={checkedIn} data-testid="me-checkin" className="btn-pressable rounded-sm h-11 bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
              <LogIn className="h-4 w-4 mr-2" /> Check in
            </Button>
            <Button onClick={checkOut} disabled={!checkedIn || checkedOut} variant="outline" data-testid="me-checkout" className="btn-pressable rounded-sm h-11 border-zinc-300 font-mono uppercase tracking-widest text-xs">
              <LogOut className="h-4 w-4 mr-2" /> Check out
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tile icon={CalendarCheck} label="Pending leaves" value={data?.pending_leaves ?? 0} accent="bg-amber-100 text-amber-700" />
        <Tile icon={Clock} label="Approved leaves" value={data?.approved_leaves ?? 0} accent="bg-emerald-100 text-emerald-700" />
        <Tile
          icon={Wallet}
          label={data?.last_payslip ? `Last payslip · ${data.last_payslip.month}` : "Last payslip"}
          value={data?.last_payslip ? formatMoney(data.last_payslip.net, data.last_payslip.currency) : "—"}
          accent="bg-orange-100 text-orange-700"
        />
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 rounded-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">{label}</div>
          <div className="font-heading text-2xl font-black tracking-tight">{value}</div>
        </div>
        <div className={`h-10 w-10 grid place-items-center rounded-sm ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
